import { AnimationTypeCardEnum, FlipMethodCardEnum } from '@vedalib/editor/lib/brand'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import cn from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'

import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsInteractiveMode } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { testProps } from 'utils/test/qaData'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import * as s from './CardElement.module.scss'
import CardSide from './CardSide'
import type { CardElementType } from './cardTypes'
import { CardSidesEnum } from './enum'
import ElementRootStyled from './styled/ElementRootStyled'
import { getBookAnimationConfig, getCardsConfig } from './utils'

const CardElement: CardElementType = ({
  element,
  styles,
  mode,
  block,
  state,
  waiting,
  setState,
  onChange,
  font,
  isV2,
}) => {
  const [hoveredCard, setHoveredCard] = useState(false)
  const [isBookAnimating, setIsBookAnimating] = useState(false)
  const bookRef = useRef<HTMLDivElement>(null)
  const isInteractive = useProjectContext(getIsInteractiveMode)
  const isBack = Boolean(state?.isBack)
  const isFill = mode.previewMode === PreviewMode.editor && mode.editorMode === AppMode.fill
  const isHoverType = styles.animation.type === FlipMethodCardEnum.hover
  const isBook = styles.animation.cardFlip === AnimationTypeCardEnum.book
  const cardsConfig = useMemo(() => getCardsConfig(isBack), [isBack])
  const bookConfig = useMemo(() => getBookAnimationConfig(isBack), [isBack])
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.card.type,
      elementId: element.id,
    },
  )

  const rtProps = useGetRichTextProps({ elementId: element.id, mode })

  const setComplete = useElementCompleted({
    block,
    elementId: element.id,
    completed: false,
    deviceMode: mode.deviceMode,
  })
  // TODO: check rebase logic
  const onFlipCard = useCallback(() => {
    if (isBook) {
      setIsBookAnimating(true)
    }
    setState?.({ isBack: !isBack, pauseAnimation: false })
    rtProps.onLabelSelect('')
    if (isInteractive && !isBack) {
      setComplete(true)
    }
    const side = isBack ? 'front' : 'back'
    const label = rtValueToText(element.value[side].label || '')
    const data = entityAnalyticsConfig.flipped({
      resultExtensions: {
        side,
        label,
      },
      objectExtensions: { totalWords: label.length, ...baseObjectExtensions },
      elementId: block?.uuid || '',
    })
    sendXapiData(data)
  }, [
    setState,
    isBack,
    rtProps,
    isInteractive,
    element.value,
    entityAnalyticsConfig,
    block?.uuid,
    setComplete,
    sendXapiData,
    baseObjectExtensions,
    isBook,
  ])

  const onClick = useCallback(() => {
    if (!isHoverType && !isFill) {
      onFlipCard()
    }
  }, [isHoverType, onFlipCard, isFill])

  const onMouseEnterCard = useCallback(() => {
    if (isHoverType && !hoveredCard && !isFill) {
      setHoveredCard(true)
      onFlipCard()
    }
  }, [hoveredCard, isHoverType, onFlipCard, isFill])

  const onMouseEnterIcon = useCallback(() => {
    if (isHoverType && !hoveredCard) {
      setHoveredCard(true)
      onFlipCard()
    }
  }, [hoveredCard, isHoverType, onFlipCard])

  const onMouseLeaveCard = useCallback(() => hoveredCard && setHoveredCard(false), [hoveredCard])

  const onTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.propertyName === 'transform' && e.target === e.currentTarget) {
      setIsBookAnimating(false)
    }
  }, [])

  useUpdateEffect(() => {
    setState?.({ isBack, pauseAnimation: true })
  }, [styles.animation.cardFlip])

  if (mode.previewMode === PreviewMode.pdf) {
    return (
      <>
        {cardsConfig.map(({ side }) => (
          <div className={s.root} key={side} style={{ ...styles.card, ...styles.border }}>
            <CardSide
              font={font}
              mode={mode}
              rtProps={rtProps}
              side={side}
              styles={styles}
              value={element.value}
              active
            />
          </div>
        ))}
      </>
    )
  }

  const flipDescriptionId = `card-flip-description-${element.id}`
  const flipTitleId = `card-flip-title-${element.id}`
  const flipTitleSideId = `card-flip-title-side-${element.id}`

  return (
    <>
      <ElementRootStyled
        $isV2={isV2}
        $noHover={isFill}
        $styles={styles}
        aria-describedby={flipDescriptionId}
        aria-labelledby={`${flipTitleId} ${flipTitleSideId}`}
        aria-pressed={isBack}
        onClick={onClick}
        onMouseEnter={onMouseEnterCard}
        onMouseLeave={onMouseLeaveCard}
      >
        <div
          className={cn(s.content, s[styles.animation.cardFlip], {
            [s.pauseAnimation]: state?.pauseAnimation,
            [s.active]: isBack,
          })}
          {...testProps({
            el: 'cardEl',
            side: isBack ? CardSidesEnum.BACK : CardSidesEnum.FRONT,
            label: isBack ? element.value.back.label : element.value.front.label,
          })}
        >
          {!isBook &&
            cardsConfig.map(({ side, active }) => (
              <CardSide
                active={active}
                font={font}
                key={side}
                mode={mode}
                onChange={onChange}
                onClick={onFlipCard}
                onMouseEnter={onMouseEnterIcon}
                rtProps={rtProps}
                side={side}
                styles={styles}
                titleId={active ? flipTitleId : undefined}
                value={element.value}
                waiting={waiting}
              />
            ))}
          {isBook && (
            <div
              className={cn(s.book, { [s.active]: isBack, [s.bookAnimating]: isBookAnimating })}
              ref={bookRef}
              style={{
                boxShadow: styles.effects.boxShadow,
                borderRadius: styles.card.borderRadius,
              }}
            >
              {bookConfig.map(({ className, side, active }, index) => (
                <div
                  className={cn(s[className], {
                    [s.transparentFront]: styles.sideA.backgroundColor === 'transparent',
                    [s.transparentBack]: styles.sideB.backgroundColor === 'transparent',
                  })}
                  key={index}
                  onTransitionEnd={onTransitionEnd}
                >
                  <CardSide
                    active={active}
                    bookRef={bookRef}
                    font={font}
                    mode={mode}
                    onChange={onChange}
                    onClick={onFlipCard}
                    onMouseEnter={onMouseEnterIcon}
                    rtProps={rtProps}
                    side={side}
                    styles={styles}
                    titleId={active ? flipTitleId : undefined}
                    value={element.value}
                    waiting={waiting}
                    offShadow
                  />
                </div>
              ))}
            </div>
          )}
          <div className={s.fill} style={{ borderRadius: styles.card.borderRadius }} />
        </div>
      </ElementRootStyled>
      <ScreenReaderOnlyText id={flipDescriptionId} hidden>
        This is a flip card. To flip the card, press Enter or Space key when focused on the card.
      </ScreenReaderOnlyText>
      <ScreenReaderOnlyText id={flipTitleSideId} hidden>
        {isBack
          ? ' The card is currently showing the back side.'
          : ' The card is currently showing the front side.'}{' '}
      </ScreenReaderOnlyText>
    </>
  )
}

export default CardElement
